/**
 * Admin: image generation configuration (used by the persona-image
 * generator). Provider/model/region/style/watermark are editable here.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, TextField, MenuItem, Switch, FormControlLabel,
  Button, Alert,
} from '@mui/material';

const PROVIDERS = [
  { value: 'vertex', label: 'Vertex AI (Google Cloud)' },
  // Future-friendly: keep these here but disabled until adapters land.
  // { value: 'openai', label: 'OpenAI' },
];

const VERTEX_MODELS = [
  'gemini-2.5-flash-image',
  'imagen-3.0-generate-002',
  'imagen-3.0-fast-generate-001',
  'imagen-3.0-generate-001',
  'gemini-3.1-flash-image-preview',
];

const VERTEX_REGIONS = ['global', 'us-central1', 'europe-west1', 'asia-northeast1'];

export default function ImageGenerationSettingsTab({ settings, handleUpdateSettings, loading }) {
  const initial = settings?.imageGenerationConfiguration || {};
  const [form, setForm] = useState({
    enabled: initial.enabled ?? true,
    provider: initial.provider || 'vertex',
    model: initial.model || 'gemini-3.1-flash-image-preview',
    region: initial.region || 'global',
    styleTemplate: initial.styleTemplate || 'A realistic, high-quality head and shoulders portrait of [Name/Description], a [Age]-year-old [Occupation]. The image is a direct-to-camera shot, framing the person as if they are on a professional video call.',
    watermark: {
      enabled: initial.watermark?.enabled ?? true,
      text: initial.watermark?.text || 'AIMHI',
    },
  });

  // Re-sync when settings prop changes (e.g. after a save round-trip).
  useEffect(() => {
    if (!settings?.imageGenerationConfiguration) return;
    const c = settings.imageGenerationConfiguration;
    setForm({
      enabled: c.enabled ?? true,
      provider: c.provider || 'vertex',
      model: c.model || 'gemini-3.1-flash-image-preview',
      region: c.region || 'global',
      styleTemplate: c.styleTemplate || form.styleTemplate,
      watermark: {
        enabled: c.watermark?.enabled ?? true,
        text: c.watermark?.text || 'AIMHI',
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.imageGenerationConfiguration]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const updateWatermark = (key, value) =>
    setForm(prev => ({ ...prev, watermark: { ...prev.watermark, [key]: value } }));

  const onSave = () => handleUpdateSettings('imageGenerationConfiguration', form);

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h6" gutterBottom>Persona image generation</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configures the model used to generate AI portraits for client personas in training scenarios.
        Generation is always available to platform admins; per-service-group lecturers can be opted in
        from each service group's simulation settings.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Generated images are watermarked before upload to prevent confusion with real clinical photographs.
      </Alert>

      <Stack spacing={2}>
        <FormControlLabel
          control={
            <Switch checked={form.enabled} onChange={(e) => update('enabled', e.target.checked)} />
          }
          label={form.enabled ? 'Image generation enabled' : 'Image generation disabled (no one can generate)'}
        />

        <TextField
          select
          label="Provider"
          value={form.provider}
          onChange={(e) => update('provider', e.target.value)}
          fullWidth
        >
          {PROVIDERS.map(p => (
            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Model"
          value={form.model}
          onChange={(e) => update('model', e.target.value)}
          fullWidth
          helperText={form.provider === 'vertex'
            ? 'Vertex Gemini image-generation models. Pick a preview model for fastest iteration.'
            : 'Provider-specific model identifier.'}
        >
          {VERTEX_MODELS.map(m => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
          {!VERTEX_MODELS.includes(form.model) && (
            <MenuItem value={form.model}>{form.model} (custom)</MenuItem>
          )}
        </TextField>

        {form.provider === 'vertex' && (
          <TextField
            select
            label="Region"
            value={form.region}
            onChange={(e) => update('region', e.target.value)}
            fullWidth
            helperText="Vertex region. Use 'global' unless your project is regionally restricted."
          >
            {VERTEX_REGIONS.map(r => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          label="Style template"
          value={form.styleTemplate}
          onChange={(e) => update('styleTemplate', e.target.value)}
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          helperText="Placeholders: [Name/Description], [Age], [Occupation], [Gender]. The persona summary (presenting concerns, physical responses) is appended automatically."
        />

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Watermark</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={form.watermark.enabled}
                onChange={(e) => updateWatermark('enabled', e.target.checked)}
              />
            }
            label={form.watermark.enabled ? 'Watermark enabled' : 'Watermark disabled'}
          />
          <TextField
            label="Watermark text"
            value={form.watermark.text}
            onChange={(e) => updateWatermark('text', e.target.value)}
            disabled={!form.watermark.enabled}
            sx={{ mt: 1 }}
          />
        </Box>

        <Box>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={loading}
          >
            {loading ? 'Saving…' : 'Save image generation settings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
